import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { ShieldAlert, Eye, EyeOff, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignupPage() {
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "", company: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.name) {
      toast.error("Name, email and password are required");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!/[A-Z]/.test(form.password)) {
      toast.error("Password must contain at least one uppercase letter");
      return;
    }
    if (!/[a-z]/.test(form.password)) {
      toast.error("Password must contain at least one lowercase letter");
      return;
    }
    if (!/[0-9]/.test(form.password)) {
      toast.error("Password must contain at least one number");
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(form.password)) {
      toast.error("Password must contain at least one special character");
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (!agreedToTerms) {
      toast.error("You must agree to the Terms & Conditions");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, {
        email: form.email,
        password: form.password,
        name: form.name,
        company: form.company,
        agreed_to_terms: true,
      });
      setRegistered(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-950">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950/30 to-slate-950" />
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(59,130,246,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(6,182,212,0.06) 0%, transparent 40%)',
        }} />
        <div className="relative z-10 flex flex-col justify-center px-16 max-w-2xl">
          <div className="flex items-center gap-4 mb-10">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl blur-sm" />
              <div className="relative h-11 w-11 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                <ShieldAlert className="h-5 w-5 text-blue-400" />
              </div>
            </div>
            <div>
              <span className="text-xl font-bold text-slate-100 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                Callintell<span className="text-blue-400">&trade;</span>
              </span>
              <p className="text-[10px] text-slate-500 tracking-[0.15em] uppercase font-medium">Digital Master Media LLC</p>
            </div>
          </div>
          <h1 className="text-4xl font-bold text-slate-100 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Start Protecting Your Calls
          </h1>
          <p className="text-lg text-slate-400 leading-relaxed">
            Create your account to access AI-powered call intelligence. Analyze calls, detect spam, and protect your business from fraudulent traffic.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-[45%] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-6 justify-center">
            <ShieldAlert className="h-6 w-6 text-blue-400" />
            <span className="text-xl font-bold text-slate-100">Callintell&trade;</span>
          </div>

          {registered ? (
            <Card className="bg-slate-900/60 border-slate-800/50 shadow-2xl">
              <CardContent className="p-8 text-center">
                <div className="h-16 w-16 rounded-full bg-emerald-500/10 border-2 border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="h-8 w-8 text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold text-slate-100 mb-2" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="account-created-title">
                  Account Created!
                </h2>
                <p className="text-sm text-slate-400 mb-6">Your account has been created successfully. You can now sign in.</p>
                <Link to="/login" className="block">
                  <Button className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white font-semibold" data-testid="go-login-after-signup">
                    Sign In
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
          <Card className="bg-slate-900/60 border-slate-800/50 shadow-2xl">
            <CardContent className="p-6 sm:p-8">
              <div className="mb-5">
                <h2 className="text-2xl font-bold text-slate-100 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Create Account</h2>
                <p className="text-sm text-slate-500">Sign up to get started</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
                <div className="space-y-1.5">
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Full Name *</Label>
                  <Input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-slate-950/60 border-slate-800/50 text-slate-100 h-11"
                    placeholder="John Smith"
                    data-testid="signup-name"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Company</Label>
                  <Input
                    type="text"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="bg-slate-950/60 border-slate-800/50 text-slate-100 h-11"
                    placeholder="Your Company Name"
                    data-testid="signup-company"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-slate-950/60 border-slate-800/50 text-slate-100 h-11"
                    placeholder="you@company.com"
                    data-testid="signup-email"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="bg-slate-950/60 border-slate-800/50 text-slate-100 h-11 pr-10"
                      placeholder="Min 8 characters"
                      data-testid="signup-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Confirm Password *</Label>
                  <Input
                    type="password"
                    value={form.confirmPassword}
                    onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                    className="bg-slate-950/60 border-slate-800/50 text-slate-100 h-11"
                    placeholder="Re-enter password"
                    data-testid="signup-confirm-password"
                  />
                </div>

                {/* Mandatory Checkbox with clickable links */}
                <div className="flex items-start gap-2.5 pt-1" data-testid="agreement-section">
                  <Checkbox
                    id="agree-terms"
                    checked={agreedToTerms}
                    onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                    className="mt-0.5 border-slate-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    data-testid="agree-terms-checkbox"
                  />
                  <label htmlFor="agree-terms" className="text-xs text-slate-400 leading-relaxed cursor-pointer select-none">
                    I agree to the{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline underline-offset-2 font-medium" data-testid="terms-link" onClick={(e) => e.stopPropagation()}>
                      Terms & Conditions
                    </a>{" "}
                    and{" "}
                    <a href="/terms#privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline underline-offset-2 font-medium" data-testid="privacy-link" onClick={(e) => e.stopPropagation()}>
                      Privacy Policy
                    </a>
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !agreedToTerms}
                  className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white font-semibold mt-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="signup-submit-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-2" /> Create Account</>}
                </Button>
              </form>

              <p className="text-center text-sm text-slate-500 mt-6">
                Already have an account?{" "}
                <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium" data-testid="login-link">
                  Sign in
                </Link>
              </p>
            </CardContent>
          </Card>
          )}
        </div>
      </div>
    </div>
  );
}
